import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CheckCircle } from "lucide-react";
import { useForm } from "@formspree/react";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

export default function QuoteDrawer() {
  const { isOpen, service, closeDrawer } = useQuoteDrawer();
  const { toast } = useToast();
  const [state, handleSubmit] = useForm("mnnvbzke");
  const [formData, setFormData] = useState({
    projectType: service || "",
    budget: "",
    customPrice: "",
    timeline: "",
    name: "",
    email: "",
    phone: "",
    description: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (service) {
      setFormData(prev => ({ ...prev, projectType: service }));
    }
  }, [service]);

  useEffect(() => {
    const savedForm = localStorage.getItem('quoteFormData');
    if (savedForm) {
      setFormData(JSON.parse(savedForm));
    }
  }, []);

  useEffect(() => {
    if (Object.values(formData).some(val => val)) {
      localStorage.setItem('quoteFormData', JSON.stringify(formData));
    }
  }, [formData]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        document.getElementById('projectType')?.focus();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.projectType) newErrors.projectType = 'Project type is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });
    
    try {
      const response = await fetch('https://formspree.io/f/xovwnnew', {
        method: 'POST',
        body: formDataToSend,
        headers: { 'Accept': 'application/json' }
      });
      
      const result = await response.json();
      
      if (result.ok) {
        setShowSuccess(true);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "There was an error submitting your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (showSuccess) {
    return (
      <motion.div 
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        className="fixed inset-0 flex items-center justify-center bg-background z-50"
      >
        <div className="text-center p-6">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 500 }}
            className="inline-block mb-4"
          >
            <CheckCircle className="h-16 w-16 text-green-500" />
          </motion.div>
          <h2 className="text-2xl font-bold mb-2">Request Submitted!</h2>
          <p className="text-muted-foreground mb-6">We'll get back to you soon</p>
          <Button onClick={() => {
            setShowSuccess(false);
            closeDrawer();
          }}>Close</Button>
        </div>
      </motion.div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={closeDrawer}
          />

          {/* Drawer - Right Side */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed top-0 right-0 h-screen w-full max-w-md bg-background border-l border-border/50 shadow-2xl z-50 flex flex-col"
          >
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6">
              <button
                onClick={closeDrawer}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close drawer"
              >
                <X className="h-5 w-5" />
              </button>
              
              <h2 className="text-2xl font-bold">Get a Quote</h2>
              <p className="text-muted-foreground">Fill out this form and we'll get back to you shortly</p>

              {/* Form */}
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="projectType">Project Type</Label>
                  <Select 
                    value={formData.projectType}
                    onValueChange={(value) => handleInputChange("projectType", value)}
                  >
                    <SelectTrigger className="[&>span]:text-muted-foreground/70 [&>span]:placeholder-opacity-100">
                      <SelectValue placeholder="Select project type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web-development">Web Development</SelectItem>
                      <SelectItem value="mobile-app">Mobile App</SelectItem>
                      <SelectItem value="cloud">Cloud Solutions</SelectItem>
                      <SelectItem value="design">UI/UX Design</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.projectType && <p className="text-red-500">{errors.projectType}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="budget">Budget Range</Label>
                    <Select 
                      value={formData.budget}
                      onValueChange={(value) => handleInputChange("budget", value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-5k">Under $5,000</SelectItem>
                        <SelectItem value="5k-15k">$5,000 - $15,000</SelectItem>
                        <SelectItem value="15k-50k">$15,000 - $50,000</SelectItem>
                        <SelectItem value="custom">Custom Amount</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.budget === 'custom' && (
                    <div>
                      <Label htmlFor="customPrice">Custom Price</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          id="customPrice"
                          type="number"
                          value={formData.customPrice}
                          onChange={(e) => handleInputChange("customPrice", e.target.value)}
                          placeholder="Enter amount"
                          className="pl-8"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="timeline">Timeline</Label>
                    <Select 
                      value={formData.timeline}
                      onValueChange={(value) => handleInputChange("timeline", value)}
                    >
                      <SelectTrigger className="[&>span]:text-muted-foreground/70 [&>span]:placeholder-opacity-100">
                        <SelectValue placeholder="Select timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-3 months">1-3 months</SelectItem>
                        <SelectItem value="3-6 months">3-6 months</SelectItem>
                        <SelectItem value="6+ months">6+ months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Name</Label>
                    <Input 
                      id="name" 
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      className="placeholder:text-muted-foreground/70"
                      placeholder="Your full name"
                      required 
                    />
                    {errors.name && <p className="text-red-500">{errors.name}</p>}
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      type="email" 
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      className="placeholder:text-muted-foreground/70"
                      placeholder="Your email address"
                      required 
                    />
                    {errors.email && <p className="text-red-500">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input 
                    id="phone" 
                    type="tel" 
                    value={formData.phone}
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    className="placeholder:text-muted-foreground/70"
                    placeholder="Your phone number"
                  />
                </div>

                <div className="relative">
                  <Label htmlFor="description">Project Details</Label>
                  <Textarea 
                    id="description" 
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    className="min-h-[120px] placeholder:text-muted-foreground/70"
                    placeholder="Describe your project requirements..."
                    maxLength={500}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                    {formData.description.length}/500
                  </div>
                </div>
              </form>
            </div>
            
            {/* Fixed footer with submit button */}
            <div className="p-4 border-t bg-background">
              <Button 
                type="submit" 
                className="w-full"
                disabled={state.submitting || isLoading}
              >
                {isLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Submit Quote Request
              </Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
